import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Inbox,
  Moon,
  Sun,
  LogOut,
  Settings2,
  HelpCircle,
} from "lucide-react";

import NavIconBtn from "./NavIconBtn";
import NavBreadcrumb from "./NavBreadcrumb";
import NavMoreMenu from "./NavMoreMenu";
import PopUpNotes from "../../Panels/PopUpNotes";
import PopUpTicket from "../../Panels/PopUpTicket";
import PopUpLogOut from "../../Panels/PopUpLogOut";
import PopUpSettings from "../../Settings/PopUpSettings";
import { useDark } from "../../Universal/useNavTheme";
import { usePopup } from "../../contexts/PopupContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLayout } from "../../contexts/LayoutContext";
import SearchTrigger from "../Search/SearchTrigger";

const COLLAPSE_WIDTH = 680;

/**
 * AnimatedA — draws the brand pyramid in sequence on mount.
 *
 * Outer triangle → inner triangle → three horizontal rungs that span
 * the full width of the outer triangle at each y-level.
 *
 * X-coords for the rungs follow the outer triangle's edge equation:
 *   left  edge: x = 3  + (42 − y) × (21/39)
 *   right edge: x = 45 − (42 − y) × (21/39)
 *
 *   y=19  →  x: 14.8–33.2  (rounded to 15–33)
 *   y=25  →  x: 11.5–36.5  (rounded to 11–37)
 *   y=31  →  x:  8.2–39.8  (rounded to  8–40)
 */
function AnimatedA({ size = 24, color = "#1c9dea" }) {
  const uid = useId().replace(/:/g, "");
  const fid = `vng-${uid}`;

  // For polygons/shapes — includes glow filter (works on 2D bounding boxes)
  const s = (dashLen, dur, delay) => ({
    stroke: color,
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    filter: `url(#${fid})`,
    strokeDasharray: dashLen,
    "--d": dashLen,
    animation: `vn-draw-${uid} ${dur}s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
  });

  // For horizontal lines — NO filter. Percentage-based filter coords collapse
  // to zero on zero-height bounding boxes, making the lines invisible.
  const ls = (dashLen, dur, delay) => ({
    stroke: color,
    fill: "none",
    strokeLinecap: "round",
    strokeDasharray: dashLen,
    "--d": dashLen,
    animation: `vn-draw-${uid} ${dur}s cubic-bezier(0.16,1,0.3,1) ${delay}s both`,
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <defs>
        <filter id={fid} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <style>{`
          @keyframes vn-draw-${uid} {
            from { stroke-dashoffset: var(--d); opacity: 0; }
            8%   { opacity: 1; }
            to   { stroke-dashoffset: 0; opacity: 1; }
          }
        `}</style>
      </defs>

      {/* ① Outer triangle — 0s */}
      <polygon
        points="24,3 45,42 3,42"
        strokeWidth="2"
        strokeLinejoin="round"
        style={s(130, 0.7, 0)}
      />

      {/* ② Inner triangle (smaller, centered) — +0.4s */}
      <polygon
        points="24,19 31,31 17,31"
        strokeWidth="1.5"
        strokeLinejoin="round"
        style={s(42, 0.5, 0.4)}
      />

      {/* ③④⑤ All start at x=5 (outer triangle far left).
           Top longest → bottom shortest, all left-anchored. */}
      <line
        x1="5"
        y1="19"
        x2="40"
        y2="19"
        strokeWidth="1.2"
        style={ls(35, 0.38, 0.75)}
      />
      <line
        x1="5"
        y1="25"
        x2="31"
        y2="25"
        strokeWidth="1.2"
        style={ls(26, 0.3, 0.75)}
      />
      <line
        x1="5"
        y1="31"
        x2="22"
        y2="31"
        strokeWidth="1.2"
        style={ls(17, 0.22, 0.75)}
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────

export default function MainNavbar() {
  const dark = useDark();
  const navigate = useNavigate();
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();
  const { darkmode, setDarkmode } = useTheme();
  const { isLeftOpen, toggleLeft, isRightOpen, toggleRight, currentAppPage } =
    useLayout();

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const check = () => setCollapsed(window.innerWidth < COLLAPSE_WIDTH);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const TUTORIAL_PAGES = ["/graph"];
  const hasTutorial = TUTORIAL_PAGES.includes(currentAppPage);

  const startTutorial = () => {
    window.dispatchEvent(
      new CustomEvent("navbar:start-tutorial", {
        detail: { page: currentAppPage },
      }),
    );
  };

  const openLogout = () => {
    setPopUpComponent(<PopUpLogOut />);
    setIsPopUpOpen(true);
  };

  const openSettings = () => {
    setPopUpComponent(<PopUpSettings />);
    setIsPopUpOpen(true);
  };

  return (
    <div className="flex flex-row z-40 h-full w-full items-center justify-between min-h-[61px] px-4 md:px-5 py-1.5 md:py-2 border-b border-mainDivider bg-mainBg shadow-md backdrop-blur-md">
      {/* ── Left: sidebar toggle + breadcrumb ── */}
      <div className="flex flex-row items-center relative gap-x-2">
        <NavIconBtn onClick={toggleLeft} aria-label="Toggle left sidebar">
          {isLeftOpen ? (
            <ChevronLeft
              strokeWidth={1.5}
              className="h-5 w-5 text-mutedText/70 transition-colors duration-200"
            />
          ) : (
            <AnimatedA size={24} />
          )}
        </NavIconBtn>
        <SearchTrigger context="navbar" />
        {/* {!isLeftOpen && <NavBreadcrumb />}*/}
      </div>

      {/* ── Right: actions ── */}
      <div className="flex flex-row items-center relative gap-x-1">
        {!collapsed ? (
          <>
            {/* Inbox with unread badge */}
            <NavIconBtn
              onClick={() => navigate("/chat")}
              aria-label="Inbox"
              title="Inbox"
            >
              <span className="relative inline-flex">
                <Inbox
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
                {/* <span className="absolute -bottom-1.5 -right-2 z-10 flex h-[14px] min-w-[14px] items-center justify-center rounded-full border border-velBlue/40 bg-mainBg px-[3px] text-[9px] font-normal leading-none text-velBlue/80">
                  2
                </span>*/}
              </span>
            </NavIconBtn>

            <PopUpTicket buttonNavbar={true} />
            {/* <PopUpNotes buttonNavbar={true} />*/}
            {/* {hasTutorial && (
              <NavIconBtn
                onClick={startTutorial}
                aria-label="Help & Tutorial"
                title="Help & Tutorial"
              >
                <HelpCircle
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              </NavIconBtn>
            )}*/}
            <NavIconBtn
              onClick={() => setDarkmode(!darkmode)}
              aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
              title={dark ? "Light Mode" : "Dark Mode"}
            >
              {dark ? (
                <Sun
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              ) : (
                <Moon
                  strokeWidth={1.5}
                  className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
                />
              )}
            </NavIconBtn>
            <NavIconBtn
              onClick={openSettings}
              aria-label="Settings"
              title="Settings"
            >
              <Settings2
                strokeWidth={1.5}
                className="h-[18px] w-[18px] text-mutedText/60 transition-colors duration-200"
              />
            </NavIconBtn>
            <NavIconBtn
              onClick={openLogout}
              aria-label="Log out"
              title="Log out"
              danger
            >
              <LogOut
                strokeWidth={1.5}
                className="h-[18px] w-[18px] text-logoutColor transition-colors duration-200"
              />
            </NavIconBtn>
          </>
        ) : (
          <NavMoreMenu
            dark={dark}
            hasTutorial={hasTutorial}
            startTutorial={startTutorial}
            openSettings={openSettings}
            openLogout={openLogout}
          />
        )}

        {/* {isRightOpen && (
          <NavIconBtn
            onClick={toggleRight}
            aria-label="Close right sidebar"
            active={isRightOpen}
          >
            <ChevronRight
              strokeWidth={1}
              className={[
                "h-5 w-5 transition-colors duration-200",
                isRightOpen ? "text-velBlue" : "text-mutedText/60",
              ].join(" ")}
            />
          </NavIconBtn>
        )}*/}
      </div>
    </div>
  );
}
